import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { SportsDataService, SportsColumnsService } from '../../services';
import { DialogSportFormComponent } from './sport-form/sport-form.component';
import { DialogSportDetailsComponent } from './sport-details/sport-details.component';
import { Sport, SportsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-sports',
  templateUrl: './sports.component.html',
  styleUrls: ['./sports.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class SportsComponent implements OnInit, OnDestroy {

  private sportAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private sportEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private sportDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public sportToDelete: Sport = {};
  public sports: Sport[] = [];
  public sport: Sport = {};
  public selectedSports: Sport[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private sportsDataService: SportsDataService,
    private sportsColumnsService: SportsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('SportsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Sports - Management');
    this.primengConfig.ripple = true;
    this.getSports();
  }

  ngOnDestroy(): void {
    console.log('SportsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getSports(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.sportsDataService.getSports(this.parameters).subscribe((response: SportsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.sports = response.results;
        } else {
          this.sports = this.sports.concat(response.results);
        }
        if (response.inlineCount > this.sports.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getSports();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SPORTSNOTLOADED')});
    });
  }

  importSports(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.sportsDataService.addSports(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('SPORTSCREATED')});
                if (proceed === j) {
                  this.getSports();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SPORTSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SPORTSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.sportsDataService.updateSports(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('SPORTSUPDATED')});
                if (proceed === j) {
                  this.getSports();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SPORTSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SPORTSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.sportsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('sports.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedSports() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Sports?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.sportsDataService.deleteSports(this.selectedSports).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Sports Deleted', life: 3000});
            this.getSports();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Sports not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Sports not deleted'});
        });
        this.selectedSports = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.sports, this.columns, 'Sports');
  }

  exportExcel() {
    ToolsService.exportExcel(this.sports, this.columns, 'Sports');
  }

  exportCsv() {
    ToolsService.exportCsv(this.sports, this.columns, 'Sports');
  }

  refresh() {
    this.sportsDataService.clearCache();
    this.getSports();
  }

  sportAdd(): void {
    if (!this.navigation.getParamValue('sports.addInDialog', true)) {
      this.router.navigate(['/sports/add']);
      return;
    }
    this.sportAddDialog = this.dialogService.open(DialogSportFormComponent, {
      header: '', // this.translate.instant('NEWSPORT'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        sport: {id: null},
        users: this.sportsColumnsService.users,
      },
    });

    this.sportAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Sport added'});
        this.getSports();
      }
    });
  }

  sportEdit(sport: Sport): void {
    if (!this.navigation.getParamValue('sports.editInDialog', true)) {
      this.router.navigate(['/sports/edit/' + sport.id]);
      return;
    }
    this.sportEditDialog = this.dialogService.open(DialogSportFormComponent, {
      header: '', // this.translate.instant('EDITSPORT') + ' ' + sport.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        sport: sport,
        users: this.sportsColumnsService.users,
      },
    });

    this.sportEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Sport updated'});
        this.getSports();
      }
    });
  }

  sportDetails(sport: Sport): void {
    if (!this.navigation.getParamValue('sports.detailsInDialog', true)) {
      this.router.navigate(['/sports/details/' + sport.id]);
      return;
    }
    this.sportDetailsDialog = this.dialogService.open(DialogSportDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        sport: sport,
      }
    });
  }

  sportDelete(sport: Sport): void {
    this.sportToDelete = sport;
    this.messageService.clear();
    this.messageService.add({
      key: 'sportDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + sport.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.sportsDataService.deleteSport(this.sportToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Sport deleted'});
      this.getSports();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Sport not deleted'});
    });
  }

  onDeleteReject(): void {
    this.sportToDelete = {};
    this.messageService.clear('sportDelete');
  }

}
